<script setup>
import { getCurrentInstance } from 'vue';
import CRError from './CRError.vue';
import CRProgress from './CRProgress.vue';
import CRManualSelection from './CRManualSelection.vue';
import CRRegenerate from './CRRegenerate.vue';
import GenerateContentDoc from '../common/GenerateContentDoc.vue';
import { isDocumentResult } from '@/utils/documentResultValidator';

const instance = getCurrentInstance();

const props = defineProps({
  result: Object,
  isDialog: Boolean,
  hasPreviousExecutions: Boolean,
  isPreviousExecution: Boolean,
  enableRegeneration: { type: Boolean, default: true },
  showDocumentActions: { type: Boolean, default: true },
  availableOutputsForRegen: Array,
  regeneratingPartial: Boolean,
  applyingDocuments: Boolean,
  selectedDocCount: { type: Number, default: 0 },
  totalDocCount: { type: Number, default: 0 },
  // Selection state
  selectionData: Object,
  selectedItems: Array,
  editedItems: null, // Map instance
  newItems: Array,
  editingItem: [Number, String],
  submittingSelection: Boolean
});

defineEmits([
  'accept', 'regenerate', 'back-to-executions', 'create-new',
  'expand-all', 'collapse-all', 'select-all-doc', 'clear-all-doc',
  // Selection events
  'toggle-item', 'select-all', 'clear-all', 'start-edit', 'save-edit',
  'cancel-edit', 'add-new', 'remove-new', 'update-field', 'submit-selection', 'cancel-selection',
  // Document events
  'doc-selection-changed'
]);

defineExpose({
  getDocumentRef(rowId) {
    return instance?.proxy?.$refs[`documentComponentRef_${rowId}`] || null;
  }
});
</script>

<template>
  <div v-if="result" class="cr-results-single">
    <!-- Error Display -->
    <CRError
      v-if="result.success === false && result.error && !result.processing"
      :error="result.error"
      :execution-details="result.executionDetails"
      @retry="$emit('create-new')"
    />

    <!-- Processing State -->
    <CRProgress
      v-else-if="result.processing"
      :result="result"
      :is-dialog="isDialog"
    />

    <!-- Manual Selection -->
    <CRManualSelection
      v-else-if="result.awaitingSelection && selectionData"
      :selection-data="selectionData"
      :selected-items="selectedItems"
      :edited-items="editedItems"
      :new-items="newItems"
      :editing-item="editingItem"
      :submitting="submittingSelection"
      @toggle-item="$emit('toggle-item', $event)"
      @select-all="$emit('select-all')"
      @clear-all="$emit('clear-all')"
      @start-edit="$emit('start-edit', $event)"
      @save-edit="$emit('save-edit', $event)"
      @cancel-edit="$emit('cancel-edit', $event)"
      @add-new="$emit('add-new')"
      @remove-new="$emit('remove-new', $event)"
      @update-field="(index, field, value) => $emit('update-field', index, field, value)"
      @submit="$emit('submit-selection')"
      @cancel="$emit('cancel-selection')"
    />

    <!-- Success State -->
    <div v-else-if="result.success" class="streamlined-result-output">
      <!-- Document Result -->
      <div v-if="isDocumentResult(result)" class="document-result">
        <div class="document-actions-header">
          <div class="document-actions">
            <div v-if="hasPreviousExecutions && isPreviousExecution">
              <v-btn small text color="primary" @click="$emit('back-to-executions')">
                <v-icon small left>mdi-arrow-left</v-icon>
                Back
              </v-btn>
            </div>
            <v-btn
              v-if="showDocumentActions"
              small
              depressed
              color="#EEEEEE"
              @click="$emit('accept')"
              :loading="applyingDocuments"
              :disabled="selectedDocCount === 0"
            >
              Apply ({{ selectedDocCount }})
            </v-btn>
            <v-btn
              small
              depressed
              color="#EEEEEE"
              @click="$emit('select-all-doc')"
              :disabled="applyingDocuments || selectedDocCount === totalDocCount"
            >
              Select All
            </v-btn>
            <v-btn
              small
              depressed
              color="#EEEEEE"
              @click="$emit('clear-all-doc')"
              :disabled="applyingDocuments || selectedDocCount === 0"
            >
              Clear All
            </v-btn>
            <v-btn small depressed color="#EEEEEE" @click="$emit('expand-all')" :disabled="applyingDocuments">
              Expand All
            </v-btn>
            <v-btn small depressed color="#EEEEEE" @click="$emit('collapse-all')" :disabled="applyingDocuments">
              Collapse All
            </v-btn>
            <CRRegenerate
              v-if="enableRegeneration"
              :result="result"
              :available-outputs="availableOutputsForRegen"
              :regenerating="regeneratingPartial"
              :is-previous-execution="isPreviousExecution"
              @regenerate="(r, key) => $emit('regenerate', r, key)"
            />
            <v-btn
              small
              depressed
              v-if="hasPreviousExecutions && isPreviousExecution"
              color="primary"
              @click="$emit('create-new')"
            >
              <v-icon left>mdi-plus-circle</v-icon>
              Create New
            </v-btn>
          </div>
        </div>

        <div :class="isDialog ? 'document-items-list-dialog' : 'document-items-list'">
          <GenerateContentDoc
            :ref="`documentComponentRef_${result.rowId}`"
            :key="`documentComponentRef_${result.rowId}`"
            :document="result"
            @selectionChanged="(data) => $emit('doc-selection-changed', result.rowId, data)"
          />
        </div>
      </div>

      <!-- Regular Text/JSON Result -->
      <div v-else class="text-result-streamlined">
        {{ result.result || 'No result generated' }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cr-results-single {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.document-actions {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
}
.text-result-streamlined {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 13px;
  padding: 12px;
  background: #fafafa;
  border-radius: 6px;
}
</style>
